import { subscribeKey } from 'valtio/utils';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';

import {
  PageVisbilityTransitionResolutionType,
  userCanSeePage,
  UserInactiveResolutionType,
  waitForPageVisibilityTransition,
  waitForUserInactive,
} from './wait';

export { UserInactiveResolutionType };

export const defaultWaitUserCanSeePage = () =>
  waitForPageVisibilityTransition(userCanSeePage);

/**
 * This will close the websocket as soon as the user becomes in activity (see: {@link waitForUserInactive}).
 *
 * The timer will for user inactivity will reset when the user sees the page (see: {@link waitForUserCanSeePage})
 *
 * @returns A function that you can use to cancel this functionality
 */
export const registerCloseOnUserInactive = (
  ws: Pick<WebSocket, 'close'>,
  waitUserInactive = waitForUserInactive,
  waitForUserCanSeePage = defaultWaitUserCanSeePage,
) => {
  const userInactiveWaiter = waitUserInactive();
  const userCanSeePageWaiter = waitForUserCanSeePage();

  let cancelCurrentTimers = () => {
    userInactiveWaiter.cancel();
    userCanSeePageWaiter.cancel();
  };

  const restartWithNewWaiters = () => {
    cancelCurrentTimers = registerCloseOnUserInactive(
      ws,
      waitUserInactive,
      waitForUserCanSeePage,
    );
  };
  userCanSeePageWaiter.promise.then((resolutionType) => {
    if (resolutionType === PageVisbilityTransitionResolutionType.CANCELLED) {
      return;
    }

    cancelCurrentTimers();

    restartWithNewWaiters();
  });

  userInactiveWaiter.promise.then(async (resolutionType) => {
    if (resolutionType === UserInactiveResolutionType.CANCELLED) {
      return;
    }

    cancelCurrentTimers();

    ws.close();
  });

  const cancel = () => cancelCurrentTimers();

  return cancel;
};

export const registerCloseOnNewSessionId = (
  ws: Pick<WebSocket, 'close' | 'readyState'>,
) => {
  const cleanup = subscribeKey(currentSessionId, 'sessionId', (newId) => {
    if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      return;
    }

    if (newId === undefined || newId === '') {
      return;
    }

    console.debug('[collab] Closing session because session ID changed to', newId);
    ws.close();

    cleanup();
  });
};

export const registerBrowserSocketCloseHandler = (
  socket: Pick<WebSocket, 'close' | 'addEventListener' | 'readyState'>,
  traceId: string,
  registerCloseOnUserInactiveDI = registerCloseOnUserInactive,
) => {
  const cancelCloseOnUserInactive = registerCloseOnUserInactiveDI(socket);
  registerCloseOnNewSessionId(socket);

  const cancel = (evt: CloseEvent) => {
    console.debug(`[collab] closed connection to relay`, { traceId, code: evt.code });
    cancelCloseOnUserInactive();
  };

  socket.addEventListener('close', cancel, { once: true });
};
